/* window.BotStudio: оболочка собрана компонентом, контракт PAGES тот же (init, mount, fill, side, openApp). */
import { definePageModule } from '../../bridge/page-module';
import { Studio } from './Studio';
import { ServerStudio } from './ServerStudio';
import { isServerMode } from '../../platform/server-mode';
import { createElement } from 'react';
import { createStudio } from './engine';
import { openStudioTarget } from './studio-navigation';
import { installStudioJobBridge } from './studio-job-bridge';
import { engineHost } from './engine-kit/engine-host';

const SKIP = new Set(['markup', 'attach', 'init', 'unmount', 'S', 'fill', 'after', 'PREFIX', 'TESTS', 'mounted']);

export interface StudioApi {
  attach: () => void;
  init: (o: BotPageUi) => void;
  unmount: () => void;
  fill: (...args: unknown[]) => void;
  S: object;
  TESTS: [string, [string, string][]][];
}

let api: StudioApi;

/** Экраны создания, которые открываются по хэшу #studio.<экран> (как кадры шаблона); остальное — browse студии */
const BOOT_SCREENS = ['circle', 'who', 'lipsync', 'photo', 'video', 'dance', 'swap', 'text', 'shoot', 'pshoot', 'collage', 'avatar', 'voice', 'cover', 'reels', 'infog', 'post', 'mcard'];

/** Hash — только подсказка навигации; шаблон и владельца заново проверяют серверные GET. */
export function studioBootTarget(hash: string): { screen?: string; appId?: number; options?: Record<string, string> } | null {
  const [section, ...flags] = hash.replace(/^#/, '').split('.');
  if (section !== 'studio') return null;
  const ids = flags.filter(value => /^native_app_[1-9][0-9]{0,15}$/.test(value));
  if (ids.length > 1) return null;
  /* Знак заказа над меню ведёт на #studio.<вид>.native_app_<id> для любого из 17 видов заказа (studio-job-bridge.ts):
     по точному id экран открывается и у видов, которых нет в списке экранов ниже (рилс, кавер, карточка…) */
  if (ids.length === 1) {
    const appId = Number(ids[0].slice(11));
    return Number.isSafeInteger(appId) && appId > 0 ? { appId } : null;
  }
  const screen = flags.find(value => BOOT_SCREENS.includes(value));
  if (!screen) return null;
  if (screen === 'video') {
    const mode = flags.includes('vdcover')
      ? 'product'
      : flags.includes('vdparallax')
        ? 'parallax'
        : flags.includes('vdtx')
          ? 'text'
          : flags.includes('vdvid') || flags.includes('vdclip')
            ? 'edit'
            : flags.includes('vdfile')
              ? 'photo'
              : undefined;
    return mode ? { screen, options: { mode } } : { screen };
  }
  if (screen === 'text') {
    /* ttyt — вход «Текст из YouTube» шаблона: экран «Текст из аудио и видео» со ссылкой (tool:'yt' остаётся во входе) */
    const mode = flags.map(value => value.replace(/^tt/, '')).find(value => ['stt', 'yt', 'seo', 'pnew', 'pphoto', 'pvideo', 'pfix'].includes(value));
    return mode ? { screen, options: { tool: mode } } : { screen };
  }
  /* «Почистить запись» и «Сменить голос в записи» шаблона (входы studio/engine.ts): vckeep — голосовым, «Убрать шум» включено; vcother — файлом */
  if (screen === 'voice' && flags.includes('vckeep')) return { screen, options: { mode: 'keep', out: 'voice', noise: 'true' } };
  if (screen === 'voice' && flags.includes('vcother')) return { screen, options: { mode: 'other', out: 'file' } };
  if (screen === 'dance' || screen === 'swap')
    return { screen: 'dance', options: { preset: screen === 'swap' || flags.includes('dnswap') ? 'swap' : 'dance' } };
  return { screen };
}

/** Экран берёт api при отрисовке: installStudio уже вызван */
export function studioApi() {
  return api;
}

/** Методы и геттеры раздела, кроме тех, что забирает контракт страницы */
function liveExtras(b: object): Record<string, unknown> {
  const extra: Record<string, unknown> = {};
  for (const [k, d] of Object.entries(Object.getOwnPropertyDescriptors(b))) {
    if (SKIP.has(k)) continue;
    if (d.get)
      Object.defineProperty(extra, k, {
        enumerable: true,
        get: () => (isServerMode() && k === 'side' ? false : (b as Record<string, unknown>)[k]),
      });
    else if (typeof d.value === 'function') {
      const fn = d.value as (...args: unknown[]) => unknown;
      extra[k] = (...args: unknown[]) => fn.apply(b, args);
    } else extra[k] = d.value;
  }
  return extra;
}

export function installStudio() {
  if (isServerMode()) {
    installStudioJobBridge();
    definePageModule({
      global: 'BotStudio',
      PREFIX: '',
      TESTS: [],
      S: {},
      Page: ServerStudio,
      init() {
        const target = studioBootTarget(location.hash);
        if (target) openStudioTarget(target);
      },
      fill(screen) {
        // При bootstrap каркас вызывает fill после init: общий экран не должен заменить точный appId или сбросить форму.
        if (location.hash.split('.')[0] === '#studio' && location.hash.split('.').some(flag => flag.startsWith('native_app_'))) return;
        if (typeof screen === 'string' && ['circle', 'who', 'lipsync', 'photo', 'video', 'dance', 'swap', 'text'].includes(screen))
          openStudioTarget(
            ['video', 'dance', 'swap', 'text'].includes(screen)
              ? (studioBootTarget(location.hash) ?? {
                  screen: screen === 'swap' ? 'dance' : screen,
                  options: screen === 'swap' ? { preset: 'swap' } : undefined,
                })
              : { screen },
          );
      },
      extra: {
        /* живые, как side/sideOpen/sideKind шаблона (studio/engine.ts): правая панель (.sd) нужна, пока активен кадр движка
           бокового экрана (engine-kit/engine-host.ts); родные формы её не просят */
        get side() {
          return engineHost.side();
        },
        get sideOpen() {
          return engineHost.sideOpen();
        },
        get sideKind() {
          return engineHost.sideKind();
        },
        // Каркас вызывает flags при запуске; демонстрационные состояния в API-режиме не применяются.
        flags: () => {},
        openApp: openStudioTarget,
        openCollection: (code: string) => openStudioTarget({ collection: code }),
        tools: () => openStudioTarget({ view: 'tools' }),
      },
    });
    return;
  }
  const b = createStudio() as unknown as StudioApi;
  api = b;
  definePageModule({
    global: 'BotStudio',
    PREFIX: '',
    TESTS: b.TESTS,
    S: b.S,
    Page: () => createElement(isServerMode() ? ServerStudio : Studio),
    init(o) {
      if (!isServerMode()) b.init(o);
    },
    fill(...args: unknown[]) {
      if (!isServerMode()) b.fill(...args);
    },
    beforeUnmount() {
      if (!isServerMode()) b.unmount();
    },
    extra: liveExtras(b),
  });
}
